import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Curve **scrvUSD** (Savings crvUSD) — intrinsic APR.
 *
 * scrvUSD is an ERC-4626 over crvUSD, and structurally it is a **Yearn V3
 * vault** that Curve operates: the share token is an EIP-1167 proxy onto
 * `YearnV3Vault.vy` v3.0.4. Curve routes crvUSD borrow revenue into it and the
 * deposit is released through Yearn's profit-unlocking rail, so the share
 * price rises for `profitMaxUnlockTime` (7 days) after each report and then
 * STOPS until the next one.
 *
 * ## Why this does not publish Curve's own APY figure
 *
 * `prices.curve.finance/v1/crvusd/savings/statistics` serves `proj_apy`, and
 * it is a **projection off `profitUnlockingRate`** — a field Yearn V3 does not
 * zero when `fullProfitUnlockDate` passes. Measured 2026-08-26: the vault's
 * last report was 2026-08-18, its unlock completed 2026-08-25, the share price
 * has been EXACTLY flat since (`convertToAssets(1e18)` identical across a full
 * day, `balanceOf(vault) == 0`), and both `profitUnlockingRate` and
 * `proj_apy` were still reporting **~1.30 %** for a vault paying nothing. The
 * API's whole record froze — `proj_apy`, `price` and `assets` were
 * byte-identical on 08-25 and 08-26 — so the staleness is not even visible as
 * a moving number a consumer could sanity-check.
 *
 * A projection off a dial that is only refreshed on the next report is exactly
 * the shape that publishes a rate through a gap. So the rate here is
 * **realized**, measured over a window long enough to span several report
 * cycles, and `proj_apy` is deliberately not read at all.
 *
 * ## The two legs
 *
 * **Primary — Curve's own daily series** (`/savings/yield`, which carries
 * `price`, `assets`, `supply` and `proj_apy` per day). We use only `price`,
 * the issuer's own recorded share price, and difference it. It needs no
 * archival RPC, and it is the same number the on-chain leg measures:
 * cross-checked 2026-08-26 at **1.219 % (series) vs 1.214 % (on-chain)** over
 * 30 days — 0.005 pp apart.
 *
 * **Fallback — the same delta read on-chain**, wren-shaped and hard-bounded
 * (2 endpoint tries, 12 s ceiling) because this runs on a path
 * `fetchSavingsVaults` awaits.
 *
 * ## Two guards, and one this one deliberately does NOT copy
 *
 * Both legs reject a share price below par — the vault launched at exactly 1.0
 * and a sub-par read means a bad response, never a loss.
 *
 * But unlike [wren](./wren.ts) and [theo](./theo.ts), the on-chain leg does
 * **not** reject a DECREASE. Those two wrap monotonic NAVs where a decrease
 * can only be a pruned node; scrvUSD is a Multi-Strategy Yearn V3 vault, and
 * although `totalDebt` is 0 today (everything idle, so the shares are backed
 * 1:1 by crvUSD sitting in the contract) the allocation is a role-holder dial.
 * If strategies are ever funded and take a loss, the share price CAN fall, and
 * a guard that rejected it would hide the one event a depositor most needs to
 * see. A pruned node errors rather than answering a smaller number, so the
 * par check already covers the failure mode this would have caught.
 */

const CHAIN_ID = '1'
/** Ethereum is the ONLY vault. The Arbitrum / Base / XDC tokens are bare
 *  bridged ERC-20s whose `asset()` and `totalAssets()` revert. */
const SCRVUSD: Address = '0x0655977feb2f289a4ab78af67bab0d17aab84367'
const ONE_E18 = 10n ** 18n

/** Trailing window. Long enough to span ~4 weekly report cycles, so a single
 *  gap between reports cannot dominate it — the 7-day figure read 1.06 %
 *  against 1.22 % at 30 days purely because of the current stall. */
const WINDOW_SECONDS = 30 * 24 * 60 * 60
const BLOCK_TIME_SECONDS = 12
const WINDOW_BLOCKS = BigInt(Math.floor(WINDOW_SECONDS / BLOCK_TIME_SECONDS))
/** Ask for more than the window so a point at least `WINDOW_SECONDS` old
 *  exists in the response. The endpoint 400s on very long ranges (a ~668-day
 *  request is refused), so this is not "just ask for everything". */
const SERIES_SPAN_SECONDS = 45 * 24 * 60 * 60

/** Small on purpose — this is on an awaited path. Same reasoning as Wren's. */
const MAX_RPC_TRIES = 2
const ONCHAIN_DEADLINE_MS = 12_000

const CURVE_SAVINGS_YIELD_URL =
  'https://prices.curve.finance/v1/crvusd/savings/yield'

export const SCRVUSD_KEY = 'SCRVUSD'
/**
 * Token-list assetGroups — **three spellings for one asset**, which is why
 * all of them are emitted:
 *
 *   1          `Savings crvUSD::SCRVUSD`
 *   42161, 50  `Savings crvUSD::scrvUSD`               ← canonical
 *   8453       `Superbridge Bridged scrvUSD::SCRVUSD`
 *
 * A GROUP_ALIAS folds the other two into the canonical form, but only from
 * the next token-list regen, and the lending join
 * (`intrinsicYields[asset.assetGroup]`) is an EXACT lookup on whatever is
 * stored today. Emitting all three keeps it working on both sides of that
 * publish — the stcUSD lesson, where one asset at one address lost its
 * intrinsic yield on the chains carrying the other spelling.
 */
export const SCRVUSD_GROUP_KEY = 'Savings crvUSD::scrvUSD'
export const SCRVUSD_GROUP_KEY_UPPER = 'Savings crvUSD::SCRVUSD'
export const SCRVUSD_GROUP_KEY_BASE = 'Superbridge Bridged scrvUSD::SCRVUSD'

const CONVERT_TO_ASSETS_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

interface CurveSavingsPoint {
  timestamp?: number
  price?: number
}

/**
 * Realized APR percent from Curve's own daily share-price series.
 *
 * Deliberately reads `price` and ignores the `proj_apy` sitting beside it —
 * see the docstring. `assets` is ignored too: the sibling `statistics`
 * endpoint's similarly-named `supply` field is SHARES, not assets (15.43M vs
 * 17.09M), and nothing good comes of getting into the habit.
 */
const fetchCurveSeriesAprPercent = async (
  nowSeconds: number,
): Promise<number> => {
  const end = Math.floor(nowSeconds)
  const start = end - SERIES_SPAN_SECONDS
  const res = await fetch(
    `${CURVE_SAVINGS_YIELD_URL}?agg_number=1&agg_units=day&start=${start}&end=${end}`,
    {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(8000),
    },
  )
  if (!res.ok) throw new Error(`Curve savings HTTP ${res.status}`)
  const json = (await res.json()) as { data?: CurveSavingsPoint[] }
  const points = (json?.data ?? [])
    .filter(
      (p): p is Required<CurveSavingsPoint> =>
        typeof p?.timestamp === 'number' &&
        typeof p?.price === 'number' &&
        p.price >= 1,
    )
    .sort((a, b) => a.timestamp - b.timestamp)
  if (points.length < 2) throw new Error('scrvUSD: series too short')

  const last = points[points.length - 1]
  // The oldest point that is still at least a full window back. Falling back
  // to `points[0]` would silently shorten the window on a truncated response,
  // which is how a stall gets annualised into a headline.
  const base = [...points]
    .reverse()
    .find((p) => p.timestamp <= last.timestamp - WINDOW_SECONDS)
  if (!base) throw new Error('scrvUSD: series does not span the window')

  const elapsed = last.timestamp - base.timestamp
  if (elapsed <= 0) throw new Error('scrvUSD: non-positive window')
  return annualizeRateDeltaPercent(last.price, base.price, elapsed)
}

const readRateAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: SCRVUSD,
    abi: CONVERT_TO_ASSETS_ABI,
    functionName: 'convertToAssets',
    args: [ONE_E18],
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

/** The same realized delta, read on-chain. Needs an archival Ethereum RPC. */
const computeAprOnChain = async (): Promise<number> => {
  let lastErr: unknown
  const deadline = Date.now() + ONCHAIN_DEADLINE_MS
  for (let rpcId = 0; rpcId < MAX_RPC_TRIES; rpcId++) {
    if (Date.now() > deadline) break
    try {
      const client = getEvmClient(CHAIN_ID, rpcId)
      const head = await client.getBlockNumber()
      const pastBlock = head > WINDOW_BLOCKS ? head - WINDOW_BLOCKS : 0n

      const [rateNow, rateThen, headBlock, thenBlock] = await Promise.all([
        readRateAt(client),
        readRateAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      // Below par is a bad read, never a loss — the vault opened at exactly
      // 1.0. A DECREASE is left alone on purpose; see the docstring.
      if (rateNow < ONE_E18 || rateThen < ONE_E18) {
        throw new Error('scrvUSD: share price below par — pruned or bad read')
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error('scrvUSD: non-positive window')

      return annualizeRateDeltaPercent(rateNow, rateThen, elapsed)
    } catch (e) {
      lastErr = e
    }
  }
  throw lastErr ?? new Error('scrvUSD: no RPC served archive state')
}

export const scrvusdFetcher: YieldFetcher = {
  label: 'SCRVUSD',
  fetch: async () => {
    const apr = await fetchCurveSeriesAprPercent(Date.now() / 1000)
      .catch(() => computeAprOnChain())
      .catch(() => 0)
    return {
      [SCRVUSD_KEY]: apr,
      [SCRVUSD_GROUP_KEY]: apr,
      [SCRVUSD_GROUP_KEY_UPPER]: apr,
      [SCRVUSD_GROUP_KEY_BASE]: apr,
    }
  },
}
